/**
 * Le récap des marges — le tableur de Romain (RECAP_MARGES_2026.xlsx), repris
 * et calculé juste.
 *
 * Un mois, des lignes ; une ligne, un chantier : facture HT, achats réels,
 * jours (« NB »), équipe (« Eq »), heures par jour (« H »). D'où :
 *   marge        = facture HT − achats
 *   marge / jour = marge ÷ jours
 *   marge / heure = marge ÷ (jours × équipe × heures)   — l'heure travaillée
 *
 * Trois défauts du tableur que ce calcul ne reproduit pas :
 *  - la marge/jour et la marge/heure du MOIS y étaient des moyennes simples :
 *    une seule ligne à −1 025 €/h (KLOUA, mars) ramenait le mois à 2,62 €/h.
 *    Ici, c'est la marge totale divisée par le temps total ;
 *  - des achats inconnus notés « 1 € » donnaient une marge presque égale à
 *    la facture. Ici, une ligne sans achats connus n'a pas de marge, et le mois
 *    le dit ;
 *  - des totaux qui oubliaient des lignes (octobre : SUM(C12:C26)) et des
 *    formules cassées (#REF!). Ici, tout se recalcule.
 */

export interface AchatRecap {
  fournisseur: string;
  montant: number;
}

export interface LigneRecap {
  id: string;
  mois: number; // 1..12
  libelle: string;
  chantierId: string | null;
  factureHt: number | null;
  jours: number | null;
  equipe: number | null;
  heuresJour: number | null;
  precisions: string;
  /** Faux : les achats ne sont pas connus, la ligne n'a pas de marge. */
  achatsConnus: boolean;
  achats: readonly AchatRecap[];
}

export interface CalculLigne {
  achats: number | null;
  marge: number | null;
  margeJour: number | null;
  margeHeure: number | null;
  /** Heures travaillées : jours × équipe × heures/jour. */
  heures: number | null;
}

const arrondi = (n: number): number => Math.round(n * 100) / 100;
const positif = (n: number | null): n is number => n !== null && Number.isFinite(n) && n > 0;

export function calculerLigneMarge(l: LigneRecap): CalculLigne {
  const achats = l.achatsConnus ? arrondi(l.achats.reduce((t, a) => t + a.montant, 0)) : null;
  const marge = achats !== null && l.factureHt !== null ? arrondi(l.factureHt - achats) : null;
  const heures =
    positif(l.jours) && positif(l.equipe) && positif(l.heuresJour)
      ? l.jours * l.equipe * l.heuresJour
      : null;
  return {
    achats,
    marge,
    margeJour: marge !== null && positif(l.jours) ? marge / l.jours : null,
    margeHeure: marge !== null && heures !== null ? marge / heures : null,
    heures,
  };
}

export interface TotalMois {
  mois: number;
  lignes: number;
  factureHt: number;
  /** Achats des lignes dont les achats sont connus. */
  achats: number;
  /** Marge des lignes dont les achats sont connus. */
  marge: number;
  /** Facturé sur des lignes dont les achats ne sont pas connus : hors marge. */
  factureSansAchats: number;
  lignesSansAchats: number;
  /** Marge ÷ facture HT, sur les seules lignes dont la marge est connue. */
  taux: number | null;
  /** Marge totale ÷ jours totaux, sur les lignes qui ont les deux. */
  margeJour: number | null;
  /** Marge totale ÷ heures totales, sur les lignes qui ont les deux. */
  margeHeure: number | null;
}

export function totaliserMois(mois: number, lignes: readonly LigneRecap[]): TotalMois {
  let factureHt = 0;
  let achats = 0;
  let marge = 0;
  let factureAvecMarge = 0;
  let factureSansAchats = 0;
  let lignesSansAchats = 0;
  let margeAvecJours = 0;
  let jours = 0;
  let margeAvecHeures = 0;
  let heures = 0;
  for (const l of lignes) {
    const c = calculerLigneMarge(l);
    factureHt += l.factureHt ?? 0;
    if (c.marge === null || c.achats === null) {
      lignesSansAchats += c.achats === null ? 1 : 0;
      factureSansAchats += l.factureHt ?? 0;
      continue;
    }
    achats += c.achats;
    marge += c.marge;
    factureAvecMarge += l.factureHt ?? 0;
    if (positif(l.jours)) {
      margeAvecJours += c.marge;
      jours += l.jours;
    }
    if (c.heures !== null) {
      margeAvecHeures += c.marge;
      heures += c.heures;
    }
  }
  return {
    mois,
    lignes: lignes.length,
    factureHt: arrondi(factureHt),
    achats: arrondi(achats),
    marge: arrondi(marge),
    factureSansAchats: arrondi(factureSansAchats),
    lignesSansAchats,
    taux: factureAvecMarge > 0 ? marge / factureAvecMarge : null,
    margeJour: jours > 0 ? margeAvecJours / jours : null,
    margeHeure: heures > 0 ? margeAvecHeures / heures : null,
  };
}

/** Le récap annuel : douze mois et l'année, tous calculés de la même façon. */
export function recapAnnuel(lignes: readonly LigneRecap[]): {
  mois: TotalMois[];
  annee: TotalMois;
} {
  const mois = Array.from({ length: 12 }, (_, i) =>
    totaliserMois(
      i + 1,
      lignes.filter((l) => l.mois === i + 1),
    ),
  );
  return { mois, annee: totaliserMois(0, lignes) };
}

// ----- Reprise du tableur -------------------------------------------------------

/** Une cellule telle que SheetJS la lit : sa valeur, et sa formule s'il y en a une. */
export interface CelluleXls {
  v: unknown;
  f?: string | undefined;
}

export interface FeuilleXls {
  nom: string;
  lignes: readonly (readonly (CelluleXls | null)[])[];
}

export interface LigneReprise {
  mois: number;
  libelle: string;
  factureHt: number | null;
  /** `null` : achats inconnus (vides, ou notés 1 € dans le tableur). */
  achats: number[] | null;
  /** Les achats valaient exactement 1 € : le tableur s'en servait pour « inconnu ». */
  achatsUnEuro: boolean;
  jours: number | null;
  equipe: number | null;
  heuresJour: number | null;
  precisions: string;
}

const MOIS_FEUILLES: readonly [RegExp, number][] = [
  [/^janv/, 1],
  [/^fevr/, 2],
  [/^mars/, 3],
  [/^avr/, 4],
  [/^mai/, 5],
  [/^juin/, 6],
  [/^juil/, 7],
  [/^aout/, 8],
  [/^sept/, 9],
  [/^oct/, 10],
  [/^nov/, 11],
  [/^dec/, 12],
];

function sansAccent(s: string): string {
  return s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
}

/** Le mois d'une feuille d'après son nom (« JANV », « FÉVR »…), ou `null`. */
export function moisDeFeuille(nom: string): number | null {
  const n = sansAccent(nom);
  return MOIS_FEUILLES.find(([re]) => re.test(n))?.[1] ?? null;
}

/** Un nombre d'une cellule : 12.5, « 0,5 », « 1 716,00 € » ; sinon `null`. */
export function nombreCellule(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v !== 'string') return null;
  const t = v.replace(/[\s\u00a0\u202f€]/g, '').replace(',', '.');
  if (t === '') return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

function texte(c: CelluleXls | null | undefined): string {
  const v = c?.v;
  if (typeof v === 'string') return v.trim();
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '';
}

/** Les achats d'une cellule : « =2500+3780+182 » donne trois achats. */
function achatsDe(c: CelluleXls | null | undefined): { achats: number[] | null; unEuro: boolean } {
  if (c === null || c === undefined) return { achats: null, unEuro: false };
  const f = (c.f ?? '').replace(/\s/g, '');
  if (/^\d+(?:\.\d+)?(?:\+\d+(?:\.\d+)?)+$/.test(f))
    return { achats: f.split('+').map(Number), unEuro: false };
  const n = nombreCellule(c.v);
  if (n === null) return { achats: null, unEuro: false };
  if (n === 1) return { achats: null, unEuro: true };
  return { achats: [n], unEuro: false };
}

const ENTETES: Record<string, keyof Colonnes> = {
  client: 'client',
  facture: 'facture',
  'facture ht': 'facture',
  achats: 'achats',
  nb: 'jours',
  eq: 'equipe',
  h: 'heures',
  precisions: 'precisions',
};

interface Colonnes {
  client: number;
  facture: number;
  achats: number;
  jours: number;
  equipe: number;
  heures: number;
  precisions: number;
}

/**
 * Les lignes du tableur, feuille par feuille. Les colonnes se retrouvent par
 * leur en-tête (« Client », « Facture HT », « Achats », « NB », « Eq », « H »,
 * « PRECISIONS ») ; la lecture s'arrête à la ligne « Total ». Ce qui est
 * calculé dans le tableur (marge, M jour, M heure, totaux) est ignoré : le CRM
 * le recalcule.
 */
export function analyserRecapMarges(feuilles: readonly FeuilleXls[]): {
  lignes: LigneReprise[];
  feuillesIgnorees: string[];
} {
  const lignes: LigneReprise[] = [];
  const feuillesIgnorees: string[] = [];
  for (const feuille of feuilles) {
    const mois = moisDeFeuille(feuille.nom);
    if (mois === null) {
      feuillesIgnorees.push(feuille.nom);
      continue;
    }
    const iEntete = feuille.lignes.findIndex((r) =>
      r.some((c) => sansAccent(texte(c)) === 'client'),
    );
    if (iEntete < 0) {
      feuillesIgnorees.push(feuille.nom);
      continue;
    }
    const col: Partial<Colonnes> = {};
    (feuille.lignes[iEntete] ?? []).forEach((c, j) => {
      const cle = ENTETES[sansAccent(texte(c)).replace(/\s+/g, ' ')];
      if (cle !== undefined && col[cle] === undefined) col[cle] = j;
    });
    if (col.client === undefined || col.facture === undefined) {
      feuillesIgnorees.push(feuille.nom);
      continue;
    }
    for (const r of feuille.lignes.slice(iEntete + 1)) {
      const libelle = texte(r[col.client]);
      if (/^total/i.test(sansAccent(libelle))) break;
      if (libelle === '') continue;
      const { achats, unEuro } = achatsDe(col.achats === undefined ? null : r[col.achats]);
      const nb = (k: keyof Colonnes): number | null => {
        const j = col[k];
        return j === undefined ? null : nombreCellule(r[j]?.v);
      };
      lignes.push({
        mois,
        libelle,
        factureHt: nb('facture'),
        achats,
        achatsUnEuro: unEuro,
        jours: nb('jours'),
        equipe: nb('equipe'),
        heuresJour: nb('heures'),
        precisions: col.precisions === undefined ? '' : texte(r[col.precisions]),
      });
    }
  }
  return { lignes, feuillesIgnorees };
}

// ----- Pré-remplir un mois à partir de ses ventes ------------------------------

/** Une vente du mois, telle que le tableau des ventes la compte. */
export interface VenteDuMois {
  numero: string;
  type: 'facture' | 'avoir';
  /** HT positif ; le type dit le sens. */
  ht: number;
  /** Le chantier, pour une facture du CRM ; `null` pour une pièce Extrabat. */
  chantierId: string | null;
  /** Ce qu'on affichera : le client pour le CRM, le numéro pour Extrabat. */
  nom: string;
}

export interface LignePreRemplie {
  libelle: string;
  chantierId: string | null;
  factureHt: number;
  precisions: string;
}

/**
 * Les lignes d'un mois tirées de ses ventes : une par chantier pour les
 * factures du CRM (avoirs déduits), une par pièce pour Extrabat — qui ne dit
 * pas à quel chantier une pièce appartient. Un chantier dont les factures et
 * avoirs s'annulent n'a pas de ligne.
 */
export function lignesDepuisVentes(ventes: readonly VenteDuMois[]): LignePreRemplie[] {
  const parChantier = new Map<string, LignePreRemplie & { numeros: string[] }>();
  const pieces: LignePreRemplie[] = [];
  for (const v of ventes) {
    const ht = v.type === 'avoir' ? -Math.abs(v.ht) : v.ht;
    if (v.chantierId === null) {
      pieces.push({ libelle: v.nom, chantierId: null, factureHt: arrondi(ht), precisions: '' });
      continue;
    }
    const l = parChantier.get(v.chantierId) ?? {
      libelle: v.nom,
      chantierId: v.chantierId,
      factureHt: 0,
      precisions: '',
      numeros: [],
    };
    l.factureHt = arrondi(l.factureHt + ht);
    l.numeros.push(v.numero);
    parChantier.set(v.chantierId, l);
  }
  const crm = [...parChantier.values()]
    .filter((l) => Math.abs(l.factureHt) >= 0.01)
    .map(({ numeros, ...l }) => ({ ...l, precisions: numeros.join(', ') }));
  return [...crm, ...pieces];
}

// ----- Exporter le récap au format du tableur ------------------------------------

/** Une cellule à écrire : une valeur, une formule, un format d'affichage. */
export interface CelluleExport {
  v: string | number | null;
  /** Formule sans le « = » (« 2500+3780 ») : garde le détail des achats. */
  f?: string;
  /** Format Excel (« #,##0.00 »). */
  z?: string;
}

export interface FeuilleExport {
  nom: string;
  lignes: CelluleExport[][];
}

const NOMS_FEUILLES = ['JANV', 'FEVR', 'MARS', 'AVRIL', 'MAI', 'JUIN', 'JUIL', 'AOUT', 'SEPT', 'OCT', 'NOV', 'DEC'];
const MOIS_MAJ = [
  'JANVIER',
  'FÉVRIER',
  'MARS',
  'AVRIL',
  'MAI',
  'JUIN',
  'JUILLET',
  'AOÛT',
  'SEPTEMBRE',
  'OCTOBRE',
  'NOVEMBRE',
  'DÉCEMBRE',
];
const EUROS = '#,##0.00';
const POURCENT = '0.0%';

const cel = (v: string | number | null, z?: string): CelluleExport =>
  z === undefined ? { v } : { v, z };

/**
 * Le récap, feuille par feuille, au format du tableur de Romain : un onglet par
 * mois (« JANV »…), une ligne par chantier, puis « RECAP ANNUEL ». Les achats
 * sont écrits en formule (« =2500+3780+182 ») : le détail survit, et le
 * fichier se reprend tel quel par « Reprendre un récap Excel ». Des achats
 * inconnus laissent la case vide — jamais 1 €.
 */
export function feuillesExportMarges(
  lignes: readonly LigneRecap[],
  /** Le nom à afficher pour un chantier relié, quand on le connaît. */
  nomChantier: (id: string) => string | null = () => null,
): FeuilleExport[] {
  const feuilles: FeuilleExport[] = [];
  const recap = recapAnnuel(lignes);
  for (const total of recap.mois) {
    if (total.lignes === 0) continue;
    const duMois = lignes.filter((l) => l.mois === total.mois);
    const rows: CelluleExport[][] = [
      [cel(MOIS_MAJ[total.mois - 1] ?? '')],
      [],
      [
        cel('Client'),
        cel('Chantier CRM'),
        cel('Facture HT'),
        cel('Achats'),
        cel('Marge'),
        cel('NB'),
        cel('M Jour'),
        cel('Eq'),
        cel('H'),
        cel('M Heure'),
        cel('PRECISIONS'),
      ],
    ];
    for (const l of duMois) {
      const c = calculerLigneMarge(l);
      const montants = l.achats.map((a) => arrondi(a.montant));
      const achats: CelluleExport =
        c.achats === null
          ? cel(null)
          : montants.length > 1 && montants.every((m) => m >= 0)
            ? { v: c.achats, f: montants.join('+'), z: EUROS }
            : cel(c.achats, EUROS);
      const fournisseurs = l.achats
        .map((a) => a.fournisseur.trim())
        .filter((f) => f !== '')
        .join('/');
      rows.push([
        cel(l.libelle),
        cel(l.chantierId === null ? null : nomChantier(l.chantierId)),
        cel(l.factureHt, EUROS),
        achats,
        cel(c.marge, EUROS),
        cel(l.jours),
        cel(c.margeJour === null ? null : arrondi(c.margeJour), EUROS),
        cel(l.equipe),
        cel(l.heuresJour),
        cel(c.margeHeure === null ? null : arrondi(c.margeHeure), EUROS),
        cel(l.precisions !== '' ? l.precisions : fournisseurs !== '' ? fournisseurs : null),
      ]);
    }
    rows.push([]);
    rows.push([
      cel('Total HT'),
      cel(null),
      cel(total.factureHt, EUROS),
      cel(total.achats, EUROS),
      cel(total.marge, EUROS),
      cel(null),
      cel(total.margeJour === null ? null : arrondi(total.margeJour), EUROS),
      cel(null),
      cel(null),
      cel(total.margeHeure === null ? null : arrondi(total.margeHeure), EUROS),
      cel(
        total.lignesSansAchats > 0
          ? `${String(total.lignesSansAchats)} ligne(s) sans achats connus, hors marge`
          : null,
      ),
    ]);
    feuilles.push({ nom: NOMS_FEUILLES[total.mois - 1] ?? String(total.mois), lignes: rows });
  }

  const annuel: CelluleExport[][] = [
    [cel('RECAP ANNUEL')],
    [],
    [
      cel('MOIS'),
      cel('LIGNES'),
      cel('CA HT'),
      cel('ACHATS'),
      cel('MARGE'),
      cel('TAUX'),
      cel('M JOUR'),
      cel('M HEURE'),
      cel('SANS ACHATS'),
    ],
  ];
  const ligneAnnuelle = (libelle: string, t: TotalMois): CelluleExport[] => [
    cel(libelle),
    cel(t.lignes),
    cel(t.factureHt, EUROS),
    cel(t.achats, EUROS),
    cel(t.marge, EUROS),
    cel(t.taux === null ? null : Math.round(t.taux * 10000) / 10000, POURCENT),
    cel(t.margeJour === null ? null : arrondi(t.margeJour), EUROS),
    cel(t.margeHeure === null ? null : arrondi(t.margeHeure), EUROS),
    cel(t.lignesSansAchats || null),
  ];
  for (const t of recap.mois) annuel.push(ligneAnnuelle(MOIS_MAJ[t.mois - 1] ?? '', t));
  annuel.push([]);
  annuel.push(ligneAnnuelle('ANNÉE', recap.annee));
  annuel.push([]);
  annuel.push([
    cel(
      'Marge, taux, marge par jour et par heure : sur les seules lignes dont les achats sont connus. Par jour et par heure : marge totale ÷ temps total.',
    ),
  ]);
  feuilles.push({ nom: 'RECAP ANNUEL', lignes: annuel });
  return feuilles;
}
